import { useCallback, useEffect, useState } from 'react'
import { authApi, type AppUser } from '@/lib/api'

/**
 * The signed-in session, backed by server httpOnly session cookies.
 * Components never touch auth directly — they use this hook.
 */
export function useSession() {
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const { user } = await authApi.me()
      setUser(user)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { user } = await authApi.signIn(email, password)
      setUser(user)
      return { user, error: null }
    } catch (e) {
      return { user: null, error: e instanceof Error ? e.message : 'Đăng nhập thất bại' }
    }
  }, [])

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    try {
      const { user } = await authApi.signUp(email, password, name)
      setUser(user)
      return { user, error: null }
    } catch (e) {
      return { user: null, error: e instanceof Error ? e.message : 'Đăng ký thất bại' }
    }
  }, [])

  const signOut = useCallback(async () => {
    await authApi.signOut().catch(() => undefined)
    setUser(null)
  }, [])

  return { user, loading, signIn, signUp, signOut, isMock: false }
}

export type { AppUser }
